<template>
  <div>
    <!-- Modal para realizar revisión -->
    <modal
      :show="visible"
      @update:show="$emit('update:visible', $event)"
      @close="handleClose"
      modal-classes="modal-dialog-centered modal-lg"
    >
      <template slot="header">
        <div class="d-flex justify-content-between align-items-center w-100">
          <h5 class="modal-title mb-0" id="exampleModalLabel">
            {{ currentIsSemiAutomatic ? "Resultado de la Revisión" : "Checklist de Revisión" }}
          </h5>
          <base-button
            v-if="step === 'checklist' && !currentIsSemiAutomatic"
            size="sm"
            type="primary"
            @click="switchToQuickRevision"
            class="ml-2"
          >
            <i class="fas fa-bolt mr-1"></i>
            Revisión Rápida
          </base-button>
        </div>
      </template>
      <div>
        <!-- Mensaje cuando no hay checklist -->
        <div v-if="checklist.length === 0 && !currentIsSemiAutomatic && step === 'checklist'" class="alert alert-warning mb-3">
          <i class="fas fa-exclamation-triangle mr-2"></i>
          <strong>Este equipo no tiene checklist asociado.</strong> No se puede realizar una revisión normal. 
          Por favor, utiliza la opción de "Revisión Rápida" para continuar.
        </div>

        <div v-if="step === 'checklist' && !currentIsSemiAutomatic && checklist.length > 0" class="revision-checklist">
          <!-- Checklist de revisión normal -->
          <div class="mb-2">
            <div
              v-for="(item, index) in checklist"
              :key="item.id"
              class="mb-3 p-3 border rounded"
            >
              <p class="mb-2 font-weight-bold">
                {{ index + 1 }}. {{ item.pregunta }}
              </p>
              <div class="row">
                <div
                  v-for="(respuesta, respIndex) in respuestas"
                  :key="respIndex"
                  class="col-md-3 mb-2"
                >
                  <button
                    class="btn btn-sm w-100"
                    :class="{
                      'btn-success': getAnswerClass(index, respIndex) === 'success',
                      'btn-danger': getAnswerClass(index, respIndex) === 'danger',
                      'btn-warning': getAnswerClass(index, respIndex) === 'warning',
                      'btn-secondary': getAnswerClass(index, respIndex) === 'secondary',
                      'btn-outline-primary': getAnswerClass(index, respIndex) === 'outline'
                    }"
                    @click="selectAnswer(index, respIndex)"
                  >
                    {{ respuesta }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="step === 'veredicto'" class="revision-veredicto">
          <!-- Selección de veredicto -->
          <div :class="currentIsSemiAutomatic ? 'mb-1' : 'mb-2'">
            <div class="row">
              <div
                v-for="(opcion, index) in veredictos"
                :key="index"
                :class="isSemiAutomatic ? 'col-md-4 mb-2' : 'col-md-4 mb-3'"
              >
                <button
                  class="btn btn-lg w-100"
                  :class="{
                    'btn-success': veredicto === index && index === 0,
                    'btn-danger': veredicto === index && index === 1,
                    'btn-warning': veredicto === index && index === 2,
                    'btn-outline-success': veredicto !== index && index === 0,
                    'btn-outline-danger': veredicto !== index && index === 1,
                    'btn-outline-warning': veredicto !== index && index === 2,
                    'disabled': currentIsSemiAutomatic && index !== 0
                  }"
                  @click="selectVeredicto(index)"
                  :disabled="currentIsSemiAutomatic && index !== 0"
                >
                  {{ opcion }}
                </button>
              </div>
            </div>
            <div v-if="currentIsSemiAutomatic" class="alert alert-info mt-2">
              <i class="fas fa-info-circle mr-2"></i>
              En revisión semiautomática solo se puede seleccionar "APTO"
            </div>
          </div>

          <!-- Observaciones -->
          <div :class="currentIsSemiAutomatic ? 'mb-1' : 'mb-2'">
            <label class="form-control-label">Observaciones</label>
            <textarea
              v-model="observaciones"
              class="form-control"
              rows="4"
              placeholder="Añade observaciones sobre la revisión..."
            ></textarea>
          </div>

          <!-- Fecha de revisión -->
          <div :class="currentIsSemiAutomatic ? 'mb-1' : 'mb-2'">
            <label class="form-control-label">Fecha de Revisión</label>
            <input
              v-model="fechaRevision"
              type="datetime-local"
              class="form-control"
            />
          </div>

          <!-- Fecha próxima revisión (por defecto +12 meses; se puede cambiar) -->
          <div :class="currentIsSemiAutomatic ? 'mb-1' : 'mb-2'">
            <label class="form-control-label">Fecha próxima revisión</label>
            <input
              v-model="fechaProximaRevision"
              type="date"
              class="form-control"
            />
            <small class="text-muted">Por defecto: 1 año desde la fecha de revisión. Puede modificarse.</small>
          </div>
        </div>

        <!-- Resumen para revisión semiautomática -->
        <div v-if="currentIsSemiAutomatic && step === 'veredicto'" class="alert alert-warning">
          <strong>Revisión Semiautomática:</strong> El checklist se completará automáticamente
          con todas las respuestas marcadas como "Sí" (Apto).
        </div>
      </div>

      <template slot="footer">
        <base-button
          type="secondary"
          @click="closeModal"
        >
          Cancelar
        </base-button>
        <base-button
          v-if="step === 'checklist' && !currentIsSemiAutomatic && checklist.length > 0"
          type="primary"
          @click="goToVeredicto"
          :disabled="!allQuestionsAnswered"
        >
          Continuar
        </base-button>
        <base-button
          v-if="checklist.length === 0 && !currentIsSemiAutomatic && step === 'checklist'"
          type="primary"
          @click="switchToQuickRevision"
        >
          <i class="fas fa-bolt mr-1"></i>
          Ir a Revisión Rápida
        </base-button>
        <base-button
          v-if="step === 'veredicto'"
          type="success"
          @click="createRevision"
          :loading="saving"
          :disabled="veredicto === -1"
        >
          {{ saving ? "Guardando..." : "Guardar Revisión" }}
        </base-button>
      </template>
    </modal>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import Modal from "@/components/Modal.vue";

export default {
  name: "RevisionModal",
  components: {
    Modal,
  },
  props: {
    activo: {
      type: Object,
      required: true,
    },
    visible: {
      type: Boolean,
      default: false,
    },
    isSemiAutomatic: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      step: "checklist", // 'checklist' o 'veredicto'
      checklist: [],
      answers: [], // Array de respuestas del checklist
      respuestas: ["Sí", "No", "A vigilar", "No aplica"],
      veredictos: ["APTO", "NO APTO", "APTO A VIGILAR"],
      veredicto: -1,
      observaciones: "",
      fechaRevision: moment().format("YYYY-MM-DDTHH:mm"),
      fechaProximaRevision: moment().add(12, "months").format("YYYY-MM-DD"),
      saving: false,
      revisor: null,
      internalIsSemiAutomatic: false, // Estado interno para controlar el modo
    };
  },
  computed: {
    allQuestionsAnswered() {
      if (this.currentIsSemiAutomatic) return true;
      return this.answers.length === this.checklist.length && 
             this.answers.every(answer => answer !== null && answer !== undefined);
    },
    currentIsSemiAutomatic() {
      return this.internalIsSemiAutomatic || this.isSemiAutomatic;
    },
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.initializeRevision();
      }
    },
  },
  methods: {
    async initializeRevision() {
      // Inicializar el estado interno con el prop
      this.internalIsSemiAutomatic = this.isSemiAutomatic;
      this.step = this.currentIsSemiAutomatic ? "veredicto" : "checklist";
      // Si es semiautomática, preseleccionar APTO (índice 0)
      this.veredicto = this.currentIsSemiAutomatic ? 0 : -1;
      this.observaciones = "";
      this.fechaRevision = moment().format("YYYY-MM-DDTHH:mm");
      this.answers = [];

      // Obtener checklist del activo
      // El activo puede venir en diferentes formatos según de dónde venga
      let checklists = null;
      
      if (this.activo?.modelo?.subtipo) {
        const subtipo = this.activo.modelo.subtipo;
        
        // Intentar obtener checklists en diferentes formatos posibles
        if (Array.isArray(subtipo.checklists)) {
          checklists = subtipo.checklists;
        } else if (subtipo.checklists?.data && Array.isArray(subtipo.checklists.data)) {
          checklists = subtipo.checklists.data;
        }
        
        // Si checklists es un array de objetos con data, extraer los datos
        if (Array.isArray(checklists) && checklists.length > 0) {
          if (checklists[0]?.data) {
            checklists = checklists.map(item => item.data?.attributes || item.data || item);
          } else if (checklists[0]?.attributes) {
            checklists = checklists.map(item => item.attributes || item);
          }
        }
      }
      
      if (checklists && Array.isArray(checklists) && checklists.length > 0) {
        this.checklist = checklists;
        
        // Si es semiautomática, pre-llenar respuestas con "Sí" (índice 0)
        if (this.currentIsSemiAutomatic) {
          this.answers = this.checklist.map(() => 0);
        } else {
          this.answers = this.checklist.map(() => null);
        }
      } else {
        this.checklist = [];
      }

      // Revisor por defecto para revisiones desde Vue (fmontoliu, id=14)
      const REVISOR_DEFAULT_ID = 14;
      this.revisor = REVISOR_DEFAULT_ID;
    },

    selectAnswer(questionIndex, answerIndex) {
      this.$set(this.answers, questionIndex, answerIndex);
    },

    getAnswerClass(questionIndex, answerIndex) {
      if (this.answers[questionIndex] === answerIndex) {
        switch (answerIndex) {
          case 0: return "success"; // Sí
          case 1: return "danger"; // No
          case 2: return "warning"; // A vigilar
          case 3: return "secondary"; // No aplica
          default: return "outline";
        }
      }
      return "outline";
    },

    selectVeredicto(index) {
      if (this.currentIsSemiAutomatic && index !== 0) {
        return; // Solo permitir APTO en semiautomática
      }
      this.veredicto = index;
    },

    switchToQuickRevision() {
      // Cambiar a modo semiautomático y saltar directamente al veredicto
      this.internalIsSemiAutomatic = true;
      this.step = "veredicto";
      
      // Pre-llenar respuestas con "Sí" (Apto) si hay checklist
      if (this.checklist.length > 0) {
        this.answers = this.checklist.map(() => 0);
      }
      
      // Pre-seleccionar APTO en revisión rápida
      this.veredicto = 0;
    },

    goToVeredicto() {
      if (this.allQuestionsAnswered) {
        this.step = "veredicto";
      }
    },

    async createRevision() {
      if (this.veredicto === -1) {
        this.$notify({
          type: "warning",
          message: "Por favor, selecciona un veredicto",
        });
        return;
      }

      this.saving = true;

      try {
        // Preparar respuestas del checklist
        const respuestas = [];
        
        if (this.currentIsSemiAutomatic) {
          // Revisión semiautomática: todas las respuestas como "Sí" (tipo_respuesta: 1)
          this.checklist.forEach((item) => {
            respuestas.push({
              pregunta: item.id,
              tipo_respuesta: 1, // 1 = Sí/Apto
            });
          });
        } else {
          // Revisión normal: usar las respuestas del usuario
          this.checklist.forEach((item, index) => {
            respuestas.push({
              pregunta: item.id,
              tipo_respuesta: this.answers[index] + 1, // +1 porque los índices empiezan en 0
            });
          });
        }

        // Mapear veredicto a estado
        const estadoMap = {
          0: "apto",
          1: "no_apto",
          2: "apto_a_vigilar",
        };

        const estadoRevisionMap = {
          0: 1, // apto -> estado_revision: 1
          1: 2, // no_apto -> estado_revision: 2
          2: 5, // apto_a_vigilar -> estado_revision: 5
        };

        const data = {
          tipo_revision: 3,
          estado_revision: estadoRevisionMap[this.veredicto],
          estado: estadoMap[this.veredicto],
          revisor: this.revisor,
          activo: this.activo.id,
          respuestas: respuestas,
          observaciones: this.observaciones,
          fecha_revision: moment(this.fechaRevision).toISOString(),
          fecha_proxima: this.fechaProximaRevision
            ? moment(this.fechaProximaRevision).startOf("day").toISOString()
            : undefined,
        };

        const apiUrl = process.env.VUE_APP_API_BASE_URL || "https://misepis-api.delfromsoftware.com/api";
        const response = await axios.post(`${apiUrl}/revisions`, { data }, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        this.$notify({
          type: "success",
          message: "Revisión creada correctamente",
        });

        this.closeModal();
        this.$emit("revision-created", response.data);
      } catch (error) {
        console.error("Error al crear revisión:", error);
        this.$notify({
          type: "danger",
          message: error.response?.data?.error?.message || "Error al crear la revisión",
        });
      } finally {
        this.saving = false;
      }
    },

    handleClose() {
      this.$emit("update:visible", false);
      this.$emit("close");
      // Resetear estado interno
      this.step = "checklist";
      this.veredicto = -1;
      this.observaciones = "";
      this.answers = [];
      this.internalIsSemiAutomatic = false;
    },
    closeModal() {
      this.handleClose();
    },
  },
};
</script>

<style scoped>
.revision-checklist {
  max-height: 60vh;
  overflow-y: auto;
}

.revision-veredicto .btn {
  min-height: 80px;
  font-size: 1.1rem;
  font-weight: bold;
}
</style>

<style>
/* Asegurar que el modal de revisión esté por encima de otros elementos, incluyendo Element UI dialogs (z-index: 2000) */
.modal.show {
  z-index: 2100 !important;
}

.modal.show .modal-dialog {
  z-index: 2101 !important;
  position: relative;
}

.modal.show .modal-content {
  z-index: 2102 !important;
  position: relative;
}

/* Backdrop del modal */
.modal.fade.show {
  z-index: 2099 !important;
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
}
</style>

